/**
 * Service catalog for the "New key" create flow.
 *
 * The user shouldn't have to classify a secret by mechanism ("is my
 * Slack token a Bearer or an API Key?") — they think in SERVICES. So
 * the create dialog is service-first: type "Google", pick it, and the
 * catalog decides the shape (`credentialType`) + provider + how to get
 * the token. Generic shapes stay as the fallback for anything unlisted.
 *
 * This is a SEARCH source, not a grid — it can grow to hundreds of
 * entries without cluttering the UI, which is what makes it both
 * friendly and sustainable. Adding a token-based service is one row
 * here (label + brand color + provider); only OAuth services (few)
 * need real per-provider code elsewhere.
 *
 * Brand marks: lucide dropped most, and we don't bundle Simple Icons,
 * so services render as a brand-tinted monogram (first letter) — clean,
 * recognizable, and no wrong-logo risk. GitHub keeps its real mark
 * (`GithubIcon` — lucide 1.x dropped brand icons).
 */
import type { CSSProperties } from 'react'
import {
  Key, KeyRound, Database, Terminal, FileJson, ShieldCheck,
  Wrench, type LucideIcon,
} from 'lucide-react'
import { GithubIcon } from '@papan/core-ui/ui'
import type { CredentialType } from '../core/types.js'
import { KEYBOOK_PROVIDERS } from '@papan/types'

export interface ServiceDef {
  id: string
  label: string
  /** extra search terms ("gcp" → Google, "db" → Postgres). */
  aliases?: string[]
  group: 'service' | 'generic'
  /** The internal shape this service maps to — drives the create form
   *  and everything downstream. Services are a friendly front door;
   *  credentialType stays the engine. */
  credentialType: CredentialType
  /** Stored on the entry row for provider-aware UI + icons. */
  provider?: string
  /** Short mechanism tag shown on the search row. */
  mech: string
  /** Monogram background (branded services). */
  brand?: string
  /** Render the real GitHub logo instead of a monogram. */
  glyph?: 'github'
  /** A neutral lucide glyph instead of a monogram (generic shapes). */
  Icon?: LucideIcon
  /** Deep-link to where the token lives, shown in the form. */
  tokenUrl?: string
  /** One-line hint under the token field. */
  tokenHelp?: string
}

export const SERVICES: ServiceDef[] = [
  // ── Popular services ──────────────────────────────────────────────
  { id: 'github', label: 'GitHub', aliases: ['git', 'repo'], group: 'service',
    credentialType: 'github', provider: 'github', mech: 'Connect', glyph: 'github' },
  // No tokenUrl on purpose: the PAT flow shows its own "create a token" link,
  // pre-filled with a name and a read-only description (EntryCreateDialog).
  { id: 'github-pat', label: 'GitHub Token', aliases: ['github pat', 'personal access token'], group: 'service',
    credentialType: 'pat', provider: 'github', mech: 'Token', glyph: 'github' },
  // provider 'gitlab' routes the token to git on gitlab.com (resolve-by-entry's
  // git route hints) — so this row is gitlab.com ONLY; a self-hosted GitLab token
  // belongs under the generic Bearer Token, or it would be offered to gitlab.com.
  { id: 'gitlab', label: 'GitLab.com', aliases: ['gitlab'], group: 'service', credentialType: 'bearer', provider: 'gitlab',
    mech: 'Token', brand: '#FC6D26', tokenUrl: KEYBOOK_PROVIDERS.gitlab!.manageUrl,
    tokenHelp: 'For gitlab.com only — for a self-hosted GitLab, use Bearer Token. Create a Personal Access Token with the scopes you need, then paste it below.' },
  { id: 'slack', label: 'Slack', group: 'service', credentialType: 'bearer', provider: 'slack',
    mech: 'Token', brand: '#4A154B', tokenUrl: KEYBOOK_PROVIDERS.slack!.manageUrl,
    tokenHelp: 'Use a Bot or User OAuth token from your Slack app.' },
  { id: 'google', label: 'Google', aliases: ['gcp', 'google cloud', 'gmail', 'workspace'], group: 'service',
    credentialType: 'service_account', provider: 'google', mech: 'JSON', brand: '#4285F4',
    tokenUrl: KEYBOOK_PROVIDERS.google!.manageUrl,
    tokenHelp: 'Paste a service-account key JSON from Google Cloud IAM.' },
  { id: 'openai', label: 'OpenAI', aliases: ['gpt', 'chatgpt'], group: 'service', credentialType: 'api_key',
    provider: 'openai', mech: 'API Key', brand: '#10A37F', tokenUrl: KEYBOOK_PROVIDERS.openai!.manageUrl },
  { id: 'stripe', label: 'Stripe', group: 'service', credentialType: 'api_key', provider: 'stripe',
    mech: 'API Key', brand: '#635BFF', tokenUrl: KEYBOOK_PROVIDERS.stripe!.manageUrl },
  { id: 'notion', label: 'Notion', group: 'service', credentialType: 'api_key', provider: 'notion',
    mech: 'API Key', brand: '#0F0F0F', tokenUrl: KEYBOOK_PROVIDERS.notion!.manageUrl },
  { id: 'asana', label: 'Asana', group: 'service', credentialType: 'bearer', provider: 'asana',
    mech: 'Token', brand: '#F06A6A', tokenUrl: KEYBOOK_PROVIDERS.asana!.manageUrl },
  { id: 'vercel', label: 'Vercel', group: 'service', credentialType: 'bearer', provider: 'vercel',
    mech: 'Token', brand: '#0F0F0F', tokenUrl: KEYBOOK_PROVIDERS.vercel!.manageUrl },
  { id: 'aws', label: 'AWS', aliases: ['amazon'], group: 'service', credentialType: 'custom', provider: 'aws',
    mech: 'Keys', brand: '#FF9900', tokenHelp: 'Add access_key_id, secret_access_key, and region as fields.' },
  { id: 'postgres', label: 'PostgreSQL', aliases: ['postgres', 'database', 'db', 'mysql', 'sql'], group: 'service',
    credentialType: 'connection_string', provider: 'postgres', mech: 'Connection', brand: '#336791' },

  // ── Generic shapes (fallback when it isn't a named service) ────────
  { id: 'g-password', label: 'Username & Password', aliases: ['login', 'password'], group: 'generic',
    credentialType: 'password', mech: 'Password', Icon: Key },
  { id: 'g-apikey', label: 'API Key', group: 'generic', credentialType: 'api_key', mech: 'API Key', Icon: KeyRound },
  { id: 'g-bearer', label: 'Bearer Token', aliases: ['token'], group: 'generic', credentialType: 'bearer', mech: 'Token', Icon: KeyRound },
  { id: 'g-conn', label: 'Connection String', aliases: ['database', 'db', 'dsn'], group: 'generic', credentialType: 'connection_string', mech: 'Connection', Icon: Database },
  { id: 'g-ssh', label: 'SSH Key', aliases: ['ssh'], group: 'generic', credentialType: 'ssh_key', mech: 'SSH', Icon: Terminal },
  { id: 'g-svc', label: 'Service Account JSON', aliases: ['json', 'service account'], group: 'generic', credentialType: 'service_account', mech: 'JSON', Icon: FileJson },
  { id: 'g-cert', label: 'Certificate', aliases: ['tls', 'ssl', 'cert'], group: 'generic', credentialType: 'certificate', mech: 'Cert', Icon: ShieldCheck },
  { id: 'g-custom', label: 'Custom', aliases: ['other', 'define your own'], group: 'generic', credentialType: 'custom', mech: 'Custom', Icon: Wrench },
]

/** Search by label / alias / mechanism. Empty query returns [] — the
 *  caller shows the ranked suggestions instead of the whole catalog.
 *  `shown` gives the words the picker DISPLAYS for a service (its localized
 *  name and tag), so a search in the UI's language finds what it shows. */
export function searchServices(q: string, shown?: (svc: ServiceDef) => string[]): ServiceDef[] {
  const s = q.trim().toLowerCase()
  if (!s) return []
  return SERVICES.filter(svc =>
    svc.label.toLowerCase().includes(s)
    || svc.aliases?.some(a => a.includes(s))
    || svc.mech.toLowerCase().includes(s)
    || (shown?.(svc) ?? []).some(w => w.toLowerCase().includes(s)),
  )
}

/** The empty-state shortlist, in a FIXED curated order. Deliberately not
 *  usage-ranked: personalization kept surfacing whatever a dev-heavy
 *  keybook happened to hold (e.g. Postgres) at the top, which reads wrong
 *  for our non-dev audience. This is the hand-picked "most people want
 *  these" order — a login first, then the common consumer/API services.
 *  Everything else stays findable by search. */
const SUGGESTED_ORDER = [
  'g-password', // Username & Password — the #1 thing a non-dev stores
  'g-apikey',   // API Key
  'google',
  'github',
  'openai',
  'g-custom',   // Custom
  'slack',
  'notion',
]

export function suggestedServices(): ServiceDef[] {
  return SUGGESTED_ORDER
    .map(id => SERVICES.find(s => s.id === id))
    .filter((s): s is ServiceDef => Boolean(s))
}

/** The service icon: real GitHub mark, a neutral lucide glyph for
 *  generic shapes, or a brand-tinted monogram for everything else. */
export function ServiceGlyph({ svc, size = 30 }: { svc: ServiceDef; size?: number }) {
  const box: CSSProperties = { width: size, height: size }
  if (svc.glyph === 'github') {
    return (
      <span className="rounded-[9px] flex items-center justify-center shrink-0 text-white" style={{ ...box, background: '#181717' }} aria-hidden>
        <GithubIcon size={Math.round(size * 0.52)} />
      </span>
    )
  }
  if (svc.Icon) {
    return (
      <span className="kbg-well rounded-[9px] flex items-center justify-center shrink-0" style={box} aria-hidden>
        <svc.Icon size={Math.round(size * 0.5)} className="themed-text" />
      </span>
    )
  }
  return (
    <span className="rounded-[9px] flex items-center justify-center shrink-0 text-white font-semibold select-none" style={{ ...box, background: svc.brand || '#6b7280', fontSize: Math.round(size * 0.42) }} aria-hidden>
      {svc.label[0]}
    </span>
  )
}
